// src/lib/chart-theme.ts
//
// Shared palette + hook for recharts charts so they adapt to the
// light/dark theme. Recharts renders inline SVG with hex/rgb colors
// (not Tailwind classes), so we need to pass strings — but those
// strings need to flip when the user toggles the theme.
//
// Usage:
//
//   "use client";
//   import { useChartPalette } from "@/lib/chart-theme";
//
//   function MyChart() {
//     const palette = useChartPalette();
//     return (
//       <CartesianGrid stroke={palette.grid} ... />
//       <XAxis tick={{ fill: palette.tick }} ... />
//       <YAxis stroke={palette.axisLine} ... />
//       <Tooltip
//         contentStyle={{
//           background: palette.tooltipBg,
//           border: `1px solid ${palette.tooltipBorder}`,
//         }}
//         labelStyle={{ color: palette.tooltipText }}
//       />
//     );
//   }
//
// Hydration: until the client mounts, we don't know the resolved theme,
// so we render with the `dark` palette (matches the historical look).
// The first paint may briefly mismatch but the swap is invisible because
// the chart's data line color stays the same; only ticks/grid change.

'use client'

import { useEffect, useState } from 'react'
import { useTheme } from 'next-themes'

export type ChartPalette = {
  /** Color of axis tick labels (numbers / week names). */
  tick: string
  /** Color of cartesian grid lines. */
  grid: string
  /** Color of axis lines themselves (often hidden). */
  axisLine: string
  /** Tooltip background fill. */
  tooltipBg: string
  /** Tooltip border color. */
  tooltipBorder: string
  /** Tooltip primary text color. */
  tooltipText: string
}

/**
 * Pre-baked dark and light palettes. Tuned so axis ticks have ≥4.5:1
 * contrast against the surrounding card background in either mode.
 */
export const CHART_PALETTES: Record<'dark' | 'light', ChartPalette> = {
  dark: {
    tick: '#cbd5e1', // slate-300 — readable on slate-900 card
    grid: '#1f2937', // gray-800 — faint but present on dark
    axisLine: '#475569', // slate-600
    tooltipBg: '#0f172a', // slate-950
    tooltipBorder: '#1e293b', // slate-800
    tooltipText: '#f1f5f9', // slate-100
  },
  light: {
    tick: '#475569', // slate-600 — readable on white
    grid: '#e2e8f0', // slate-200 — faint but present on light
    axisLine: '#cbd5e1', // slate-300
    tooltipBg: '#ffffff',
    tooltipBorder: '#e2e8f0', // slate-200
    tooltipText: '#0f172a', // slate-900
  },
}

/**
 * Returns the chart palette matching the current theme. Defaults to
 * `dark` until the client mounts (avoids hydration mismatch on
 * server-rendered pages).
 */
export function useChartPalette(): ChartPalette {
  const { resolvedTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  if (!mounted) return CHART_PALETTES.dark
  return resolvedTheme === 'light' ? CHART_PALETTES.light : CHART_PALETTES.dark
}

/**
 * Distinct, high-contrast colors for grouped scatter series (e.g. two industries).
 * Chosen to read on both the light "paper" (force-light test surface) and dark
 * cards, and to be distinguishable by the most common color-vision deficiencies
 * (green vs blue vs amber vs purple, not red/green-only).
 */
export const CHART_GROUP_COLORS = [
  '#22a06b', // green
  '#3b82f6', // blue
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ef4444', // red
  '#06b6d4', // cyan
] as const
